import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import apiService from '../services/api';
import { hasValidAuthSession, redirectToLogin } from '../utils/authSession';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const validateSession = useCallback(async () => {
    if (!hasValidAuthSession()) {
      apiService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setSessionError(null);
      setIsChecking(false);
      return false;
    }

    try {
      const currentUser = await apiService.verifySession();
      setUser(currentUser);
      setIsAuthenticated(true);
      setSessionError(null);
      return true;
    } catch {
      apiService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setSessionError('Votre session a expiré. Reconnectez-vous pour continuer.');
      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    void validateSession();
  }, [validateSession]);

  useEffect(() => {
    const onFocus = () => {
      if (!hasValidAuthSession()) {
        redirectToLogin();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onFocus();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  if (isChecking) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, minHeight: '40vh' }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Vérification de la session…
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ sessionExpired: Boolean(sessionError) }} />;
  }

  if (!apiService.userHasSuperAdminRole(user)) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6, px: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Ce compte n’a pas le rôle <strong>ROLE_SUPER_ADMIN</strong> en base de données. Le super-admin refuse
          l’accès (vous resteriez sinon sur la page login sans explication).
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rôles reçus de l’API : {(user?.roles ?? []).join(', ') || 'aucun'}
        </Typography>
        <Button variant="contained" onClick={() => { apiService.logout(); window.location.href = '/login'; }}>
          Retour connexion
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
